import { useCallback, useRef, useState } from "react";
import {
  cn,
  formatBytes,
  formatPixels,
  validateImageFile,
} from "@/lib/utils";

export interface SourceImage {
  file: File;
  previewUrl: string;
  width: number;
  height: number;
}

interface DropzoneProps {
  source: SourceImage | null;
  disabled?: boolean;
  onSelect: (file: File) => void;
  onClear: () => void;
  onInvalid: (message: string) => void;
}

export function Dropzone({
  source,
  disabled,
  onSelect,
  onClear,
  onInvalid,
}: DropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [hover, setHover] = useState(false);

  const handleFiles = useCallback(
    (list: FileList | null) => {
      const file = list?.[0];
      if (!file) return;
      const check = validateImageFile(file);
      if (!check.ok) {
        onInvalid(check.error || "Invalid image.");
        return;
      }
      onSelect(file);
    },
    [onInvalid, onSelect],
  );

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
        className="sr-only"
        disabled={disabled}
        onChange={(event) => {
          handleFiles(event.target.files);
          event.currentTarget.value = "";
        }}
      />

      {!source ? (
        <button
          type="button"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
          onDragEnter={(event) => {
            event.preventDefault();
            setHover(true);
          }}
          onDragOver={(event) => {
            event.preventDefault();
            event.dataTransfer.dropEffect = "copy";
            setHover(true);
          }}
          onDragLeave={() => setHover(false)}
          onDrop={(event) => {
            event.preventDefault();
            setHover(false);
            handleFiles(event.dataTransfer.files);
          }}
          className={cn(
            "crop-marks relative flex min-h-[440px] w-full flex-col items-start justify-end overflow-hidden border-[3px] border-ink p-6 text-left transition sm:min-h-[500px] sm:p-8",
            hover ? "bg-acid shadow-smash-tape" : "bg-cream shadow-smash",
            disabled && "pointer-events-none opacity-50",
          )}
        >
          <span className="absolute right-4 top-4 rotate-[-8deg] border-[3px] border-ink bg-tape px-2 py-1 font-slam text-sm uppercase text-ink animate-stamp">
            No upload
          </span>
          <span className="absolute left-4 top-16 border-[3px] border-dashed border-ink px-2 py-1 font-mono text-[10px] uppercase tracking-[0.14em]">
            Plate A · Source
          </span>
          <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.16em]">
            01 / slam a file here
          </p>
          <p className="mt-3 max-w-md font-slam text-5xl uppercase leading-[0.9] sm:text-6xl">
            Drop it
            <br />
            or click it
          </p>
          <p className="mt-4 max-w-sm text-sm font-medium leading-relaxed">
            JPG, PNG, WEBP. 10 MB ceiling. File stays in the tab. We don&apos;t
            get a copy.
          </p>
        </button>
      ) : (
        <div className="panel overflow-hidden">
          <div className="flex items-center justify-between gap-3 border-b-[3px] border-ink bg-crate px-4 py-3 text-cream">
            <div className="min-w-0">
              <p className="truncate font-mono text-xs">{source.file.name}</p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.12em] text-cream/60">
                {formatPixels(source.width, source.height)} ·{" "}
                {formatBytes(source.file.size)}
              </p>
            </div>
            <button
              type="button"
              onClick={onClear}
              disabled={disabled}
              className="btn-ghost bg-acid text-ink"
            >
              Dump
            </button>
          </div>
          <button
            type="button"
            disabled={disabled}
            onClick={() => inputRef.current?.click()}
            className="block w-full bg-ink"
            aria-label="Replace source image"
          >
            <img
              src={source.previewUrl}
              alt="Source"
              className="max-h-[500px] w-full object-contain"
            />
          </button>
        </div>
      )}
    </div>
  );
}
